// src/pages/services/CyberSecurity.jsx - Premium Security Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowLeft, ArrowRight, Shield, Lock, Eye, AlertTriangle, FileSearch } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function CyberSecurity() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Penetration Testing',
      description: 'Comprehensive security testing to identify vulnerabilities',
      features: ['Network Testing', 'Web App Testing', 'API Testing', 'Mobile App Testing']
    },
    {
      icon: <Eye className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Security Audits',
      description: 'In-depth security assessments and compliance audits',
      features: ['Code Review', 'Infrastructure Audit', 'Compliance Check', 'Risk Assessment']
    },
    {
      icon: <Lock className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Security Implementation',
      description: 'Deploy enterprise-grade security solutions',
      features: ['Firewall Setup', 'IDS/IPS', 'WAF Configuration', 'Encryption']
    },
    {
      icon: <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: 'Incident Response',
      description: '24/7 monitoring and rapid incident response',
      features: ['Threat Monitoring', 'Incident Handling', 'Forensics', 'Recovery']
    }
  ];

  const threats = [
    {
      name: 'Ransomware',
      icon: '🔒',
      impact: 'High',
      description: 'Malware that encrypts data and demands payment',
      protection: 'Regular backups, email filtering, employee training'
    },
    {
      name: 'Phishing',
      icon: '🎣',
      impact: 'Medium',
      description: 'Deceptive emails to steal credentials',
      protection: 'Email security, MFA, awareness training'
    },
    {
      name: 'SQL Injection',
      icon: '💉',
      impact: 'Critical',
      description: 'Database attacks through web applications',
      protection: 'Input validation, parameterized queries, WAF'
    },
    {
      name: 'DDoS Attacks',
      icon: '🌊',
      impact: 'High',
      description: 'Overwhelming servers with traffic',
      protection: 'DDoS mitigation, CDN, rate limiting'
    }
  ];

  const securityStats = [
    { value: '100%', label: 'Vulnerability Detection', icon: <FileSearch className="w-6 h-6 sm:w-8 sm:h-8" /> },
    { value: '24/7', label: 'Security Monitoring', icon: <Eye className="w-6 h-6 sm:w-8 sm:h-8" /> },
    { value: '< 1hr', label: 'Incident Response', icon: <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8" /> },
    { value: '99.9%', label: 'Threat Block Rate', icon: <Shield className="w-6 h-6 sm:w-8 sm:h-8" /> }
  ];

  const securityTools = [
    { name: 'Burp Suite', icon: '🔍', category: 'Penetration Testing' },
    { name: 'Metasploit', icon: '⚔️', category: 'Exploitation' },
    { name: 'Wireshark', icon: '🦈', category: 'Network Analysis' },
    { name: 'Nmap', icon: '🗺️', category: 'Network Scanning' },
    { name: 'OWASP ZAP', icon: '⚡', category: 'Web Testing' },
    { name: 'Snort', icon: '🐽', category: 'IDS/IPS' }
  ];

  const pricingPlans = [
    {
      name: 'Security Audit',
      price: 12999,
      monthlySupport: 499,
      color: 'red',
      badge: null,
      features: [
        'Vulnerability Assessment',
        'Basic Penetration Testing',
        'Code Review',
        '2 Months Support Included',
        'Detailed Security Report',
        'Remediation Recommendations'
      ]
    },
    {
      name: 'Advanced Testing',
      price: 34999,
      monthlySupport: 1999,
      color: 'rose',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Full Penetration Testing',
        'Advanced Code Audit',
        'Infrastructure Assessment',
        '4 Months Support Included',
        'Compliance Verification',
        'Detailed Attack Simulations',
        'Executive Summary Reports'
      ]
    },
    {
      name: 'Managed Security',
      price: null,
      monthlySupport: 2999,
      color: 'pink',
      badge: null,
      features: [
        '24/7 Security Monitoring',
        'Dedicated Security Team',
        'Incident Response (1-hour SLA)',
        'Monthly Threat Assessments',
        'Forensic Analysis',
        'Continuous Vulnerability Scanning',
        'Compliance Management'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-red-600 to-pink-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                Cyber Security Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-red-100 mb-6 sm:mb-8 leading-relaxed">
                Protect your business from cyber threats with comprehensive security solutions. Expert penetration testing, security audits, and 24/7 monitoring.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-red-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Get Security Audit
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View Security Cases
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&h=400&fit=crop"
                alt="Cyber Security"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Security Stats */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            {securityStats.map((stat, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="flex justify-center text-red-600 mb-2 sm:mb-3 w-6 h-6 sm:w-8 sm:h-8 mx-auto group-hover:scale-110 transition-transform">
                  {stat.icon}
                </div>
                <div className="text-2xl sm:text-4xl font-black text-red-600 mb-1 sm:mb-2">{stat.value}</div>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our Security Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              Comprehensive cybersecurity solutions to protect your business
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center text-red-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cyber Threats Section */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-red-600/10 via-pink-600/10 to-rose-600/10 px-2 sm:px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Common Cyber Threats We Protect Against
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Understanding and defending against modern security threats
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-8">
            {threats.map((threat, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-xl transition">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="text-4xl sm:text-5xl flex-shrink-0">{threat.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2 sm:mb-3 gap-2 flex-wrap">
                      <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{threat.name}</h3>
                      <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                        threat.impact === 'Critical' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                        threat.impact === 'High' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' :
                        'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300'
                      }`}>
                        {threat.impact} Risk
                      </span>
                    </div>
                    <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-2 sm:mb-3">{threat.description}</p>
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-2 sm:p-3">
                      <p className="text-xs sm:text-sm font-semibold text-green-800 dark:text-green-300 mb-1">Our Protection:</p>
                      <p className="text-xs sm:text-sm text-green-700 dark:text-green-400">{threat.protection}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Tools */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-gray-800 to-gray-900 dark:from-gray-900 dark:to-black px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-white">
            Security Tools & Technologies
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {securityTools.map((tool, index) => (
              <div key={index} className="bg-gray-700/50 dark:bg-gray-800/50 backdrop-blur-sm p-3 sm:p-6 rounded-lg shadow-lg text-center hover:scale-105 transition border border-gray-600/30">
                <div className="text-3xl sm:text-5xl mb-2 sm:mb-3">{tool.icon}</div>
                <h3 className="font-semibold text-xs sm:text-base text-white mb-1">{tool.name}</h3>
                <p className="text-xs text-gray-300">{tool.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Process */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            Our Security Testing Process
          </h2>
          <div className="grid md:grid-cols-5 gap-3 sm:gap-6">
            {[
              { step: '1', title: 'Reconnaissance', desc: 'Information gathering' },
              { step: '2', title: 'Scanning', desc: 'Vulnerability detection' },
              { step: '3', title: 'Exploitation', desc: 'Attempt penetration' },
              { step: '4', title: 'Reporting', desc: 'Detailed findings' },
              { step: '5', title: 'Remediation', desc: 'Fix vulnerabilities' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-red-600 to-pink-600 text-white rounded-full flex items-center justify-center text-lg sm:text-2xl font-black mx-auto mb-2 sm:mb-4 shadow-lg group-hover:scale-110 transition-transform">
                  {item.step}
                </div>
                <h3 className="text-sm sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{item.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              Security Service Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Transparent pricing for security solutions. From audits to managed 24/7 protection.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                red: { bg: 'from-red-600 to-pink-600', light: 'from-red-50 to-pink-50', hover: 'hover:border-red-200' },
                rose: { bg: 'from-rose-600 to-pink-600', light: 'from-rose-50 to-pink-50', hover: 'hover:border-rose-200' },
                pink: { bg: 'from-pink-600 to-rose-600', light: 'from-pink-50 to-rose-50', hover: 'hover:border-pink-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-rose-500 dark:border-rose-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-rose-500/10 to-pink-500/10 dark:from-rose-600/20 dark:to-pink-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'Security Audit' && 'Perfect to assess risks'}
                    {plan.name === 'Advanced Testing' && 'Best for thorough protection'}
                    {plan.name === 'Managed Security' && 'Complete ongoing defense'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'red' ? '#dc2626, #ec4899' : plan.color === 'rose' ? '#e11d48, #ec4899' : '#ec4899, #be185d'})`}}>
                            ${plan.price.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'red' ? '#dc2626, #ec4899' : plan.color === 'rose' ? '#e11d48, #ec4899' : '#ec4899, #be185d'})`}}>
                            ${plan.monthlySupport.toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'red' ? '#dc2626, #ec4899' : plan.color === 'rose' ? '#e11d48, #ec4899' : '#ec4899, #be185d'})`}}>
                          ${plan.monthlySupport.toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'red' ? '#fee2e2' : plan.color === 'rose' ? '#ffe4e6' : '#fce7f3'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'red' ? '#dc2626' : plan.color === 'rose' ? '#e11d48' : '#ec4899'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Managed Security' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '📋', text: 'Executive Report' },
                { icon: '🔐', text: 'Remediation Guide' },
                { icon: '📞', text: 'Priority Support' },
                { icon: '📊', text: 'Risk Assessment' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-2xl border border-red-200/50 dark:border-red-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">Cybersecurity FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'How long does a penetration test take?',
                  a: 'Security audits: 1-2 weeks. Full penetration tests: 3-4 weeks. Managed services available ongoing. Timeline depends on scope and infrastructure complexity.'
                },
                {
                  q: 'What is included in your security audit?',
                  a: 'Vulnerability scanning, code review, infrastructure assessment, threat modeling, risk ranking, and detailed remediation recommendations with proof-of-concept exploits.'
                },
                {
                  q: 'Do you provide compliance certifications?',
                  a: 'Yes! We help with SOC 2, ISO 27001, HIPAA, GDPR, PCI-DSS, and other compliance requirements. Our reports are accepted by auditors and regulators.'
                },
                {
                  q: 'What happens if you find vulnerabilities?',
                  a: 'We provide detailed documentation with severity levels, business impact, and step-by-step remediation guides. No disclosure without your approval.'
                },
                {
                  q: 'Can you re-test after we fix issues?',
                  a: 'Absolutely! All plans include verification testing to confirm vulnerabilities are properly remediated. We track fixes through to completion.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-red-600 to-pink-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Shield className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-pulse" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Protect Your Business Today
          </h2>
          <p className="text-base sm:text-xl text-red-100 mb-6 sm:mb-8 px-2">
            Don't wait for a breach. Get a comprehensive security assessment and protect your business from cyber threats now.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-red-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>🛡️ Get Free Security Assessment</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="cyber-security" />
      </div>
    </div>
  );
}